// Firestore service functions for recipes
import { db } from '../config/firebase.js';
import { 
  collection, 
  doc, 
  getDocs, 
  getDoc, 
  addDoc, 
  updateDoc, 
  deleteDoc,
  query,
  where,
  orderBy 
} from 'firebase/firestore';

// Get default recipes (public, read-only)
export const getDefaultRecipes = async () => {
  try {
    const recipesSnapshot = await getDocs(
      query(collection(db, 'recipes'), where('isDefault', '==', true))
    );
    const recipes = {};
    
    recipesSnapshot.forEach((doc) => {
      recipes[doc.id] = {
        id: doc.id,
        ...doc.data()
      };
    });
    
    return recipes;
  } catch (error) {
    console.error('Error fetching default recipes:', error);
    throw error;
  }
};

// Get user-specific recipes from Firestore
export const getUserRecipes = async () => {
  try {
    // Import auth here to avoid circular dependencies
    const { auth } = await import('../config/firebase.js');
    
    // Check if user is authenticated
    if (!auth.currentUser) {
      throw new Error('User not authenticated. Please sign in to access your recipes.');
    }
    
    const recipesSnapshot = await getDocs(
      query(
        collection(db, 'recipes'), 
        where('userId', '==', auth.currentUser.uid)
      )
    );
    const recipes = {};
    
    recipesSnapshot.forEach((doc) => {
      const data = doc.data();
      // Ensure userId exists, is not empty, and matches current user
      if (data.userId && 
          data.userId.trim() !== '' && 
          data.userId === auth.currentUser.uid) {
        recipes[doc.id] = {
          id: doc.id,
          ...data
        };
      }
    });
    
    return recipes;
  } catch (error) {
    console.error('Error fetching user recipes:', error);
    throw error;
  }
};

// Get all recipes (for backward compatibility)
export const getAllRecipes = async (useUserRecipes = false) => {
  if (useUserRecipes) {
    return await getUserRecipes();
  } else {
    return await getDefaultRecipes();
  }
};

// Get a single recipe by ID (checks both default and user recipes)
export const getRecipeById = async (recipeId) => {
  try {
    const recipeDoc = await getDoc(doc(db, 'recipes', recipeId));
    
    if (recipeDoc.exists()) {
      const recipeData = recipeDoc.data();
      const { auth } = await import('../config/firebase.js');
      
      // Allow access to default recipes or user's own recipes
      if (recipeData.isDefault || 
          (auth.currentUser && recipeData.userId === auth.currentUser.uid)) {
        return {
          id: recipeDoc.id,
          ...recipeData
        };
      } else {
        throw new Error('Access denied. You can only view your own recipes.');
      }
    } else {
      console.warn(`Recipe with ID ${recipeId} not found`);
      return null;
    }
  } catch (error) {
    console.error('Error fetching recipe:', error);
    throw error;
  }
};

// Search recipes by title
export const searchRecipes = async (searchTerm, useUserRecipes = false) => {
  try {
    let recipesQuery;
    
    if (useUserRecipes) {
      const { auth } = await import('../config/firebase.js');
      if (!auth.currentUser) {
        throw new Error('User not authenticated. Please sign in to search your recipes.');
      }
      
      recipesQuery = query(
        collection(db, 'recipes'), 
        where('userId', '==', auth.currentUser.uid)
      );
    } else {
      recipesQuery = query(
        collection(db, 'recipes'), 
        where('isDefault', '==', true)
      );
    }
    
    const recipesSnapshot = await getDocs(recipesQuery);
    const recipes = [];
    
    recipesSnapshot.forEach((doc) => {
      const data = doc.data();
      
      // Check if title matches search term
      if (data.title && data.title.toLowerCase().includes(searchTerm.toLowerCase())) {
        // Additional validation for user recipes
        if (useUserRecipes) {
          // Get auth again for validation
          const { auth: authValidation } = require('../config/firebase.js');
          if (data.userId && 
              data.userId.trim() !== '' && 
              authValidation.currentUser &&
              data.userId === authValidation.currentUser.uid) {
            recipes.push({
              id: doc.id,
              ...data
            });
          }
        } else if (data.isDefault === true) {
          recipes.push({
            id: doc.id,
            ...data
          });
        }
      }
    });
    
    return recipes;
  } catch (error) {
    console.error('Error searching recipes:', error);
    throw error;
  }
};

// Get recipes by meal type
export const getRecipesByMeal = async (mealType, useUserRecipes = false) => {
  try {
    let recipesQuery;
    
    if (useUserRecipes) {
      const { auth } = await import('../config/firebase.js');
      if (!auth.currentUser) {
        throw new Error('User not authenticated. Please sign in to access your recipes.');
      }
      
      recipesQuery = query(
        collection(db, 'recipes'), 
        where('userId', '==', auth.currentUser.uid),
        where('meal', '==', mealType)
      );
    } else {
      recipesQuery = query(
        collection(db, 'recipes'), 
        where('isDefault', '==', true),
        where('meal', '==', mealType)
      );
    }
    
    const recipesSnapshot = await getDocs(recipesQuery);
    const recipes = [];
    
    recipesSnapshot.forEach((doc) => {
      const data = doc.data();
      
      // For user recipes, double-check userId exists, is not empty, and matches
      if (useUserRecipes) {
        // Get auth again for validation (auth is available in the parent scope for user recipes)
        const { auth: authValidation } = require('../config/firebase.js');
        if (data.userId && 
            data.userId.trim() !== '' && 
            authValidation.currentUser &&
            data.userId === authValidation.currentUser.uid) {
          recipes.push({
            id: doc.id,
            ...data
          });
        }
      } else {
        // For default recipes, ensure they're marked as default
        if (data.isDefault === true) {
          recipes.push({
            id: doc.id,
            ...data
          });
        }
      }
    });
    
    return recipes;
  } catch (error) {
    console.error(`Error fetching ${mealType} recipes:`, error);
    throw error;
  }
};

// Add a new recipe (user-specific)
export const addRecipe = async (recipeData) => {
  try {
    const { auth } = await import('../config/firebase.js');
    
    if (!auth.currentUser) {
      throw new Error('User not authenticated. Please sign in to add recipes.');
    }
    
    const docRef = await addDoc(collection(db, 'recipes'), {
      ...recipeData,
      userId: auth.currentUser.uid,
      isDefault: false,
      createdAt: new Date(),
      updatedAt: new Date()
    });
    
    console.log('Recipe added with ID:', docRef.id);
    return docRef.id;
  } catch (error) {
    console.error('Error adding recipe:', error);
    throw error;
  }
};

// Update an existing recipe
export const updateRecipe = async (recipeId, updates) => {
  try {
    await updateDoc(doc(db, 'recipes', recipeId), {
      ...updates,
      updatedAt: new Date()
    });
    
    console.log('Recipe updated successfully');
  } catch (error) {
    console.error('Error updating recipe:', error);
    throw error;
  }
};

// Delete a recipe
export const deleteRecipe = async (recipeId) => {
  try {
    await deleteDoc(doc(db, 'recipes', recipeId));
    console.log('Recipe deleted successfully');
  } catch (error) {
    console.error('Error deleting recipe:', error);
    throw error;
  }
};